import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Tab,
    Tabs,
    Tooltip,
    Typography,
} from "@mui/material"
import { Stack } from "@mui/system"
import { useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Config, DailySchedule, INITIAL_STATE_ARMED, Schedule } from "../../../api/AutoArmConfig"
import { updatedSet } from "../../../config/set"
import { SaveButton } from "../../common/SaveButton"
import { AutoArmCustom } from "./AutoArmCustom"
import { AutoArmToggle } from "./AutoArmToggle"
import { AutoArmWeekly } from "./AutoArmWeekly"
import { ScheduleSetter } from "./ScheduleSetter"

enum TabId {
    DailySchedule = 0,
    DaysOfWeekOverrides = 1,
    DateRangeOverrides = 3,
}

type Props = {
    initialConfig: Config
    readOnly: boolean
    onClose: () => void
    saveConfig: (config?: Schedule, enabled?: boolean) => Promise<boolean>
}

const normalizeDailySchedule = (s?: Partial<DailySchedule>): DailySchedule => ({
    initial: s?.initial ?? INITIAL_STATE_ARMED,
    transitions: s?.transitions ?? [],
})

const normalizedConfig = (config?: Schedule): Schedule => ({
    schedule: normalizeDailySchedule(config?.schedule),
    overrides:
        config?.overrides?.map((o) => ({
            condition: o.condition,
            schedule: normalizeDailySchedule(o.schedule),
        })) ?? [],
    timezone: config?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
})

export const AutoArmConfigDialog = ({ initialConfig, readOnly, onClose, saveConfig }: Props) => {
    const { t } = useTranslation()
    const [tabIndex, setTabIndex] = useState(0)
    const [tabErrors, setTabErrors] = useState(new Set<TabId>())
    const [enabled, setEnabled] = useState(initialConfig.enabled)
    const [config, setConfig] = useState<Schedule>(normalizedConfig(initialConfig.schedule))

    const isAnyTabError = useMemo(() => Object.values(tabErrors).some((v) => v), [tabErrors])

    const updateTabErrors = useCallback(
        (tabIndex: number, hasError: boolean) => setTabErrors((prev) => updatedSet(prev, tabIndex, hasError)),
        [setTabErrors]
    )

    const tabErrorStyle = {
        borderBottom: "2px solid red",
    }

    return (
        <Dialog open={true} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>
                <Stack direction="row" sx={{ alignItems: "center" }} spacing={2}>
                    <Typography variant="h5">{t("alarm.auto_arm_configuration")}</Typography>
                    <AutoArmToggle
                        value={enabled}
                        disabled={readOnly}
                        onChange={(v) => {
                            setEnabled(v)
                        }}
                    />
                </Stack>
            </DialogTitle>

            <DialogContent>
                <Tabs value={tabIndex} onChange={(_, newIndex) => setTabIndex(newIndex)}>
                    <Tab label={t("daily")} sx={tabErrors.has(TabId.DailySchedule) ? tabErrorStyle : {}} />
                    <Tab label={t("weekly")} sx={tabErrors.has(TabId.DaysOfWeekOverrides) ? tabErrorStyle : {}} />
                    <Tab label={t("custom")} sx={tabErrors.has(TabId.DateRangeOverrides) ? tabErrorStyle : {}} />
                </Tabs>

                {tabIndex === 0 && (
                    <ScheduleSetter
                        readOnly={readOnly || !enabled}
                        value={config.schedule}
                        onChange={(schedule) => setConfig((prev) => ({ ...prev, schedule }))}
                        onError={(hasError) => updateTabErrors(TabId.DailySchedule, hasError)}
                    />
                )}

                {tabIndex === 1 && (
                    <AutoArmWeekly
                        value={config.overrides}
                        readOnly={readOnly || !enabled}
                        onChange={(overrides) => setConfig((prev) => ({ ...prev, overrides }))}
                        onError={(hasError) => updateTabErrors(TabId.DaysOfWeekOverrides, hasError)}
                    />
                )}

                {tabIndex === 2 && (
                    <AutoArmCustom
                        value={config.overrides}
                        readOnly={readOnly || !enabled}
                        onChange={(overrides) => setConfig((prev) => ({ ...prev, overrides }))}
                        onError={(hasError) => updateTabErrors(TabId.DateRangeOverrides, hasError)}
                    />
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>{t("action.cancel")}</Button>
                <Tooltip
                    title={readOnly ? t("message.no_change_permission_tooltip") : ""}
                    disableHoverListener={!readOnly}
                >
                    <div>
                        <SaveButton
                            disabled={isAnyTabError || readOnly || tabErrors.size > 0}
                            onClick={() => saveConfig(config, enabled)}
                            onSuccess={() => onClose()}
                            label={t("action.save")}
                        />
                    </div>
                </Tooltip>
            </DialogActions>
        </Dialog>
    )
}
